import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { getScrollbarWidth } from "./Helpers";
import { ContainerSizeInfo } from "./ContainerSizeInfo";
import { ImageInfo } from "./ImageInfo";
import { useMeasureElement } from "./CustomHooks";
import { range, rgbColorAToHex } from "./MathHelpers";
import { useComponentEditorContext } from "./ComponentEditorContext";
import { useTreeModifyContext } from "./TreeDataContext";
import { usePathContext } from "./PathInfo";
import { ClickableAreaDefinition, ClickableAreasData } from "./ClickableAreasData";

interface PositionsCanvasGridProps {
  backgroundImage: ImageInfo | undefined;
  data: ClickableAreasData;
  zoom: number;
  containerSize: ContainerSizeInfo;
  width: number;
  height: number;
}

export const ClickableAreasCanvasGrid: React.FC<PositionsCanvasGridProps> = ({
  backgroundImage,
  data,
  zoom,
  containerSize,
  width,
  height,
}) => {
  const { getVisual } = useComponentEditorContext();
  const [foregroundCanvasRef, setForegroundCanvasRef] = useState<HTMLCanvasElement | null>(null);
  const [imageSizeInfo, setImageSizeInfo] = useState({
    width: 0,
    height: 0,
    scaledWidth: 0,
    scaledHeight: 0,
    dx: 0,
    dy: 0,
  });
  const [canvasContainerRef, setCanvasContainerRef] = useState<HTMLDivElement>();
  const containerOverflow = useMeasureElement(canvasContainerRef, zoom);
  const [backgroundElementRef, setBackgroundElementRef] = useState<HTMLElement | null>();

  const { saveChangesAtPath } = useTreeModifyContext();
  const { path } = usePathContext();

  const measureCanvasContainer = useCallback(
    (node: any) => {
      if (node === null) {
        return;
      }
      setCanvasContainerRef(node);
    },
    [setCanvasContainerRef],
  );

  const measuredForegroundCanvasRef = useCallback(
    (node: any) => {
      if (node === null && node !== foregroundCanvasRef) {
        return;
      }
      setForegroundCanvasRef(node);
    },
    [foregroundCanvasRef],
  );

  const handleCanvasClick = useCallback(
    (event: MouseEvent) => {
      if (!foregroundCanvasRef || !foregroundCanvasRef) return;
      const rect = foregroundCanvasRef?.getBoundingClientRect();

      const scaleX = foregroundCanvasRef.width / rect.width;
      const scaleY = foregroundCanvasRef.height / rect.height;

      const clickX = (event.clientX - rect.left) * scaleX - imageSizeInfo.dx;
      const clickY = (event.clientY - rect.top) * scaleY - imageSizeInfo.dy;

      // const gridX = Math.round(clickX / (cellSizeX * zoom));
      // const gridY = Math.round(clickY / (cellSizeY * zoom));
      //
      // toggleDot(gridX, gridY);
    },
    [foregroundCanvasRef, imageSizeInfo.dx, imageSizeInfo.dy],
  );

  const drawGrid = useCallback(
    (ctxFront: CanvasRenderingContext2D) => {
      if (!imageSizeInfo.dx || !imageSizeInfo.dy || !imageSizeInfo.width || !imageSizeInfo.height) return;
      const canvasWidth = ctxFront.canvas.width;
      const canvasHeight = ctxFront.canvas.height;
      const horizontalStep = (imageSizeInfo.width / width) * zoom;
      const verticalStep = (imageSizeInfo.height / height) * zoom;
      const cellOffsetX = imageSizeInfo.dx % horizontalStep;
      const cellOffsetY = imageSizeInfo.dy % verticalStep;
      const minAlfaScaling = 1;
      const maxAlfaScaling = 15;
      const lineAlfaH = Math.round(range(maxAlfaScaling, minAlfaScaling, 255, 0, verticalStep));
      const lineAlfaW = Math.round(range(maxAlfaScaling, minAlfaScaling, 255, 0, horizontalStep));
      const tenLineAlfaH = Math.round(range(maxAlfaScaling, minAlfaScaling, 255, 20, verticalStep));
      const tenLineAlfaW = Math.round(range(maxAlfaScaling, minAlfaScaling, 255, 20, horizontalStep));
      const lineColorH = rgbColorAToHex(0, 0, 0, lineAlfaH);
      const lineColorW = rgbColorAToHex(0, 0, 0, lineAlfaW);
      const tenLineColorH = rgbColorAToHex(255, 0, 0, tenLineAlfaH);
      const tenLineColorW = rgbColorAToHex(255, 0, 0, tenLineAlfaW);

      let lineIndexY = Math.ceil(-imageSizeInfo.dy / verticalStep);
      for (let y = cellOffsetY; y <= canvasHeight; y += verticalStep) {
        ctxFront.strokeStyle = lineColorH;

        if (lineIndexY % 10 === 0) {
          ctxFront.strokeStyle = tenLineColorH;
        }
        ctxFront.lineWidth = 1;
        if (lineIndexY % 100 === 0) {
          ctxFront.strokeStyle = rgbColorAToHex(0, 0, 0, 255);
          ctxFront.lineWidth = 2;
        }

        ctxFront.beginPath();
        ctxFront.moveTo(0, y);
        ctxFront.lineTo(canvasWidth, y);
        ctxFront.stroke();
        lineIndexY++;
      }

      let lineIndexX = Math.ceil(-imageSizeInfo.dx / horizontalStep);
      for (let x = cellOffsetX; x <= canvasWidth; x += horizontalStep) {
        ctxFront.strokeStyle = lineColorW;
        if (lineIndexX % 10 === 0) {
          ctxFront.strokeStyle = tenLineColorW;
        }
        ctxFront.lineWidth = 1;
        if (lineIndexX % 100 === 0) {
          ctxFront.strokeStyle = rgbColorAToHex(0, 0, 0, 255);
          ctxFront.lineWidth = 2;
        }

        ctxFront.beginPath();
        ctxFront.moveTo(x, 0);
        ctxFront.lineTo(x, canvasHeight);
        ctxFront.stroke();
        lineIndexX++;
      }

      // console.log(`I drew ${lineIndexY-Math.ceil(-imageSizeInfo.dy / verticalStep)} lines on Y and ${lineIndexX - Math.ceil(-imageSizeInfo.dx / horizontalStep)} lines on X`);
    },
    [imageSizeInfo.dx, imageSizeInfo.dy, imageSizeInfo.width, imageSizeInfo.height, width, zoom, height],
  );

  const drawAreas = useCallback(
    (ctxFront: CanvasRenderingContext2D, area: ClickableAreaDefinition, areaName: string) => {
      ctxFront.save();
      let smallestFontSize = Number.MAX_VALUE;

      const areaLeft = ((area.left * zoom) / width) * imageSizeInfo.width + imageSizeInfo.dx;
      const areaTop = ((area.top * zoom) / height) * imageSizeInfo.height + imageSizeInfo.dy;
      const areaWidth = ((area.width * zoom) / width) * imageSizeInfo.width;
      const areaHeight = ((area.height * zoom) / height) * imageSizeInfo.height;

      ctxFront.beginPath();
      ctxFront.lineWidth = 1;
      ctxFront.strokeStyle = "rgba(0, 0, 0, 1)";
      ctxFront.fillStyle = "rgba(0, 255, 255, 0.5)";
      ctxFront.rect(areaLeft, areaTop, areaWidth, areaHeight);
      // Draw a cross on the dot width of 3px
      ctxFront.lineWidth = 3;
      ctxFront.fill();
      ctxFront.stroke();

      let fontSize = 64;
      let textWidth;
      let textAscent;
      let textDescent;
      do {
        ctxFront.font = `bold ${fontSize}px Times New Roman`;
        ctxFront.textAlign = "center";
        ctxFront.textBaseline = "middle";
        const textMetrics = ctxFront.measureText(areaName);
        textWidth = textMetrics.width;
        textAscent = textMetrics.fontBoundingBoxAscent;
        textDescent = textMetrics.actualBoundingBoxDescent;
        fontSize -= 1; // Decrease font size until it fits
      } while (textWidth > areaWidth || textAscent > areaHeight / 2 || textDescent > areaHeight / 2);

      ctxFront.font = `bold ${fontSize}px Times New Roman`;
      ctxFront.fillStyle = "rgba(30,30,30,1)";
      ctxFront.textAlign = "center";
      ctxFront.textBaseline = "middle";
      ctxFront.fillText(areaName, areaLeft + areaWidth / 2, areaTop + areaHeight / 2);
      ctxFront.restore();
    },
    [zoom, width, imageSizeInfo.width, imageSizeInfo.dx, imageSizeInfo.height, imageSizeInfo.dy, height],
  );

  const drawImage = useCallback(() => {
    let childElement = backgroundElementRef?.children[0] as HTMLElement;
    if (!childElement) return;
    if (!canvasContainerRef) return;
    if (!foregroundCanvasRef) return;

    const width = childElement.offsetWidth;
    const height = childElement.offsetHeight;
    const scaledWidth = width * zoom;
    const scaledHeight = height * zoom;
    let dx: number = (foregroundCanvasRef.width - scaledWidth) / 2;
    let dy: number = (foregroundCanvasRef.height - scaledHeight) / 2;
    setImageSizeInfo({ width, height, scaledWidth, scaledHeight, dx, dy });
  }, [backgroundElementRef?.children, canvasContainerRef, foregroundCanvasRef, zoom]);

  const backgroundElement = useMemo(() => {
    if (backgroundImage) {
      return getVisual(
        backgroundImage,
        (newRef) => {
          setBackgroundElementRef(newRef);
          drawImage();
        },
        {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        },
        { transform: `scale(${zoom})` },
      );
    }
  }, [backgroundImage, drawImage, getVisual, zoom]);

  const updateCanvas = useCallback(
    (ctxFront: CanvasRenderingContext2D) => {
      ctxFront.resetTransform();
      ctxFront.clearRect(0, 0, ctxFront.canvas.width, ctxFront.canvas.height);
      drawImage();
      drawGrid(ctxFront);
      if (data) {
        Object.keys(data).forEach((key) => {
          drawAreas(ctxFront, data[key], key);
        });
      }
    },
    [drawImage, drawGrid, data, drawAreas],
  );

  useEffect(() => {
    if (foregroundCanvasRef) {
      foregroundCanvasRef.addEventListener("click", handleCanvasClick);
    }

    return () => {
      if (foregroundCanvasRef) {
        foregroundCanvasRef.removeEventListener("click", handleCanvasClick);
      }
    };
  }, [foregroundCanvasRef, handleCanvasClick]);

  useEffect(() => {
    const scrollbarWidth = getScrollbarWidth();
    const ctxFront = foregroundCanvasRef?.getContext("2d");
    if (!foregroundCanvasRef || !ctxFront || !backgroundElementRef) return;

    const containerWidth =
      containerSize.width - 1 - (containerOverflow.getSizeInfo.isOverflowH ? scrollbarWidth + 1 : 0);
    const containerHeight =
      containerSize.height - 1 - (containerOverflow.getSizeInfo.isOverflowW ? scrollbarWidth + 1 : 0);
    let widthNumber = Math.max(imageSizeInfo.scaledWidth + 20 * 2 * zoom, containerWidth);
    let heightNumber = Math.max(imageSizeInfo.scaledHeight + 20 * 2 * zoom, containerHeight);
    foregroundCanvasRef.width = widthNumber;
    backgroundElementRef.style.width = "" + widthNumber + "px";
    foregroundCanvasRef.height = heightNumber;
    backgroundElementRef.style.height = "" + heightNumber + "px";

    updateCanvas(ctxFront);
  }, [
    canvasContainerRef,
    zoom,
    containerSize.width,
    containerSize.height,
    updateCanvas,
    containerOverflow.getSizeInfo.isOverflowH,
    containerOverflow.getSizeInfo.isOverflowW,
    imageSizeInfo.scaledHeight,
    imageSizeInfo.scaledWidth,
    foregroundCanvasRef,
    backgroundElementRef,
  ]);

  useEffect(() => {
    const ctxFront = foregroundCanvasRef?.getContext("2d");
    if (!foregroundCanvasRef || !ctxFront) return;
    updateCanvas(ctxFront);
  }, [foregroundCanvasRef, data, updateCanvas]);

  return (
    <Box className="canvas-container" ref={measureCanvasContainer}>
      {/*<canvas ref={backgroundCanvasRef} className="canvas-layer"/>*/}
      {backgroundElement}
      <canvas ref={measuredForegroundCanvasRef} className="canvas-layer" />
    </Box>
  );
};
